import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";
import GoalsService from "../services/GoalsService";
import {
  measureBody,
  measureEyebrow,
  measurePrimaryButtonLabel,
  measureSectionTitle,
} from "../utils/measurePageTypography";

const DIABETES_OPTIONS = ["Type 1", "Type 2", "Prediabetic", "None"] as const;
export type DiabetesFocusOption = (typeof DIABETES_OPTIONS)[number];

type DiabetesFocusSheetProps = {
  open: boolean;
  onClose: () => void;
  /** API value e.g. `type2` */
  currentApiType?: string;
  saving?: boolean;
  onSave: (apiType: string) => void | Promise<void>;
};

const DiabetesFocusSheet = ({
  open,
  onClose,
  currentApiType = "none",
  saving = false,
  onSave,
}: DiabetesFocusSheetProps) => {
  const [selected, setSelected] = useState<DiabetesFocusOption>("Type 2");

  useEffect(() => {
    if (open) {
      setSelected(
        GoalsService.mapDiabetesTypeFromAPI(currentApiType) as DiabetesFocusOption,
      );
    }
  }, [open, currentApiType]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleSave = () => {
    onSave(GoalsService.mapDiabetesTypeToAPI(selected));
  };

  return (
    <div className="fixed inset-0 z-[200] flex flex-col justify-end" role="presentation">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden
      />

      <div className="relative z-10 mx-auto w-full max-w-[400px] duration-200 animate-in slide-in-from-bottom-4">
        <div className="max-h-[min(85dvh,_720px)] overflow-y-auto scrollbar-hide rounded-t-3xl border border-border bg-background shadow-2xl ring-1 ring-border/80">
          <div className="flex justify-center pb-2 pt-3">
            <div className="h-1 w-11 rounded-full bg-muted-foreground/25" aria-hidden />
          </div>

          <div className="px-5 pb-1 pt-0.5">
            <p className={measureEyebrow}>Your focus · Change</p>
          </div>

          <div className="space-y-5 px-5 pb-[max(1.25rem,env(safe-area-inset-bottom,1rem))] pt-2">
            <h2 className={measureSectionTitle}>Type of your diabetes</h2>
            <p className={measureBody}>
              Same as Goals on your profile — updates your personalization focus.
            </p>

            <div className="space-y-2">
              {DIABETES_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  disabled={saving}
                  onClick={() => setSelected(option)}
                  className={cn(
                    "w-full rounded-2xl border-2 px-4 py-3.5 text-left text-callout font-semibold transition-colors",
                    selected === option
                      ? "border-primary/40 bg-[hsl(var(--primary-light))]/80 text-foreground ring-1 ring-primary/20"
                      : "border-border bg-card text-foreground hover:border-primary/25 hover:bg-muted/50",
                  )}
                >
                  {option === "Type 1" || option === "Type 2" ? (
                    <>
                      {option}
                      <span className="ml-1 font-normal text-muted-foreground">diabetes</span>
                    </>
                  ) : (
                    option
                  )}
                </button>
              ))}
            </div>

            <Button
              type="button"
              className={`h-12 w-full rounded-2xl bg-gradient-primary text-subhead font-semibold shadow-primary hover:opacity-90 ${measurePrimaryButtonLabel}`}
              disabled={saving}
              onClick={handleSave}
            >
              {saving ? "Saving…" : "Save focus"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiabetesFocusSheet;
